import React, {createContext, useContext} from 'react'

export interface PomNavigationConfig {
  /** Build the path to the plants global overview */
  buildOverviewPath: () => string
  /** Build the path to a plant's dashboard. Receives the plant's upmId. */
  buildDashboardPath: (upmId: string) => string
  /** Build the path to a plant section. Receives the plant's upmId. */
  buildSectionPath: (upmId: string, sectionId: string) => string
  /** Build the path to a plant sub-section. Receives the plant's upmId. */
  buildSubSectionPath: (upmId: string, sectionId: string, topicId: string) => string
}

const PomNavigationContext = createContext<PomNavigationConfig | undefined>(undefined)

export const PomNavigationProvider: React.FC<{
  config: PomNavigationConfig
  children: React.ReactNode
}> = ({config, children}) => (
  <PomNavigationContext.Provider value={config}>{children}</PomNavigationContext.Provider>
)

export const usePomNavigation = (): PomNavigationConfig => {
  const ctx = useContext(PomNavigationContext)
  if (!ctx) {
    throw new Error('usePomNavigation must be used within PomNavigationProvider')
  }
  return ctx
}

// ---- Plant Navigation ID ----
// Replaces the previously used URL-based usePlantCode hook.
// Each entrypoint provides the appropriate value:
// - POM standalone: upm id from components and URL :plantId param (operated on plantId)
// - Suite: upmId (from URL :hierarchyId param)

const PlantNavIdContext = createContext<string | undefined>(undefined)

export const PlantNavIdProvider: React.FC<{
  id: string
  children: React.ReactNode
}> = ({id, children}) => (
  <PlantNavIdContext.Provider value={id}>{children}</PlantNavIdContext.Provider>
)

export const usePlantNavId = (): string => {
  const id = useContext(PlantNavIdContext)
  if (!id) {
    throw new Error('usePlantNavId must be used within PlantNavIdProvider')
  }
  return id
}
